import { useState, useEffect } from "react";
import "@/App.css";
import { motion, AnimatePresence } from "framer-motion";
import {
  Phone,
  Mail,
  MapPin,
  Menu,
  X,
  ChevronRight,
  Armchair,
  PaintRoller,
  Hammer,
  Star,
  Check,
  Send,
  ArrowRight,
  Palette,
  Home as HomeIcon,
  Paintbrush,
  Wallpaper,
  Droplets,
  Sun
} from "lucide-react";
import { Button } from "./components/ui/button";
import { Card, CardContent } from "./components/ui/card";
import { Input } from "./components/ui/input";
import { Textarea } from "./components/ui/textarea";

// Data - Easy to modify
const COMPANY_INFO = {
  name: "Smoravi Interiors",
  tagline: "Transforming Spaces into Masterpieces",
  phone: "+917208803647",
  phoneDisplay: "+917208803647",
  whatsapp: "917208803647",
  email: "smoravi.support@gmail.com",
  address: "Santacruz, Mumbai, Maharashtra",
  mapUrl: "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d30168.843687457453!2d72.82669!3d19.0825!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3be7c9d4e9bca5eb%3A0xf2e1d5e69c7a95e3!2sSantacruz%2C%20Mumbai%2C%20Maharashtra!5e0!3m2!1sen!2sin!4v1635000000000!5m2!1sen!2sin"
};

const SERVICES = [
  {
    id: 1,
    title: "Interior Design",
    description: "Full-service interior styling for modern homes. From concept to completion.",
    icon: Armchair,
    color: "bg-orange-50 text-orange-600",
    borderColor: "border-orange-200"
  },
  {
    id: 2,
    title: "Professional Painting",
    description: "Wall painting, textures, and weather-proofing with premium quality paints.",
    icon: PaintRoller,
    color: "bg-teal-50 text-teal-600",
    borderColor: "border-teal-200"
  },
  {
    id: 3,
    title: "Renovation",
    description: "Complete structural and aesthetic makeovers for your dream space.",
    icon: Hammer,
    color: "bg-rose-50 text-rose-600",
    borderColor: "border-rose-200"
  },
  {
    id: 4,
    title: "False Ceiling",
    description: "Modern ceiling designs with POP, gypsum, and creative lighting solutions.",
    icon: HomeIcon,
    color: "bg-blue-50 text-blue-600",
    borderColor: "border-blue-200"
  },
  {
    id: 5,
    title: "Wallpaper Installation",
    description: "Wide range of wallpaper designs professionally installed with precision.",
    icon: Wallpaper,
    color: "bg-purple-50 text-purple-600",
    borderColor: "border-purple-200"
  },
  {
    id: 6,
    title: "Waterproofing",
    description: "Protect your walls and roof from leakage with expert waterproofing.",
    icon: Droplets,
    color: "bg-cyan-50 text-cyan-600",
    borderColor: "border-cyan-200"
  }
];

const PROJECTS = [
  {
    id: 1,
    title: "Modern Living Room",
    category: "Interior",
    image: "https://images.unsplash.com/photo-1616593871468-2a9452218369?w=800&q=80",
    span: "md:col-span-8"
  },
  {
    id: 2,
    title: "Cozy Bedroom",
    category: "Painting",
    image: "https://images.unsplash.com/photo-1612301988752-5a5b19021f45?w=800&q=80",
    span: "md:col-span-4"
  },
  {
    id: 3,
    title: "Minimalist Kitchen",
    category: "Renovation",
    image: "https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=800&q=80",
    span: "md:col-span-4"
  },
  {
    id: 4,
    title: "Elegant Dining",
    category: "Interior",
    image: "https://images.unsplash.com/photo-1613282556715-9b33416983de?w=800&q=80",
    span: "md:col-span-8"
  },
  {
    id: 5,
    title: "Home Office",
    category: "Interior",
    image: "https://images.unsplash.com/photo-1629737273704-5c96a46f63e6?w=800&q=80",
    span: "md:col-span-6"
  },
  {
    id: 6,
    title: "Master Suite",
    category: "Painting",
    image: "https://images.unsplash.com/photo-1618219908412-a29a1bb7b86e?w=800&q=80",
    span: "md:col-span-6"
  }
];

const TESTIMONIALS = [
  {
    id: 1,
    name: "Prakash Sharma",
    role: "Homeowner, Andheri",
    content: "Smoravi Interiors transformed our 2BHK into a stunning modern home. The attention to detail and quality of work exceeded our expectations!",
    image: "https://images.unsplash.com/photo-1622461828449-9cbb6d0b7d72?w=200&q=80",
    rating: 5
  },
  {
    id: 2,
    name: "Rajesh Patel",
    role: "Business Owner, Bandra",
    content: "Professional team, excellent workmanship. They completed our office renovation on time and within budget. Highly recommended!",
    image: "https://images.unsplash.com/photo-1622461828279-f028ae93a63b?w=200&q=80",
    rating: 5
  },
  {
    id: 3,
    name: "Vikram Bansal",
    role: "Homeowner, Santacruz",
    content: "The painting work done by Smoravi is impeccable. Clean, precise, and they left no mess behind. Will definitely hire again!",
    image: "https://images.unsplash.com/photo-1717964028300-f2861ae1bf8a?w=200&q=80",
    rating: 5
  }
];

const PRICING = [
  {
    id: 1,
    title: "Basic",
    price: "Starting from",
    amount: "₹15",
    unit: "per sq.ft",
    features: [
      "Single coat painting",
      "Basic wall preparation",
      "Standard paint options",
      "1 year warranty"
    ],
    popular: false
  },
  {
    id: 2,
    title: "Premium",
    price: "Starting from",
    amount: "₹25",
    unit: "per sq.ft",
    features: [
      "Double coat painting",
      "Wall putty & primer",
      "Premium paint brands",
      "Texture options",
      "3 year warranty"
    ],
    popular: true
  },
  {
    id: 3,
    title: "Luxury",
    price: "Starting from",
    amount: "₹45",
    unit: "per sq.ft",
    features: [
      "Triple coat painting",
      "Complete wall treatment",
      "Designer finishes",
      "Custom textures",
      "5 year warranty",
      "Free touch-up service"
    ],
    popular: false
  }
];

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

// WhatsApp Icon Component
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Header Component
const Header = ({ isScrolled, activeSection }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const navLinks = [
    { name: "Home", href: "#home" },
    { name: "Services", href: "#services" },
    { name: "Projects", href: "#projects" },
    { name: "About", href: "#about" },
    // { name: "Pricing", href: "#pricing" },
    { name: "Contact", href: "#contact" }
  ];

  return (
    <header 
      data-testid="header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? "glass border-b border-slate-100/50 shadow-sm" : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <a href="#home" className="flex items-center gap-2" data-testid="logo">
            <div className="w-10 h-10 rounded-full bg-teal-600 flex items-center justify-center">
              <Palette className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-xl text-slate-900" style={{ fontFamily: "'Playfair Display', serif" }}>
              {COMPANY_INFO.name}
            </span>
          </a>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className={`nav-link text-sm font-medium tracking-wide transition-colors ${
                  activeSection === link.href.slice(1) ? "text-teal-600" : "text-slate-600 hover:text-slate-900"
                }`}
              >
                {link.name}
              </a>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden md:block">
            <a href={`https://wa.me/${COMPANY_INFO.whatsapp}`} target="_blank" rel="noopener noreferrer">
              <Button 
                data-testid="header-cta"
                className="bg-teal-600 hover:bg-teal-700 text-white rounded-full px-6 gap-2"
              >
                <WhatsAppIcon className="w-4 h-4" />
                Get Quote
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden p-2 text-slate-600"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-t border-slate-100"
            data-testid="mobile-menu"
          >
            <nav className="container mx-auto px-4 py-4 flex flex-col gap-2">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={() => setMobileMenuOpen(false)}
                  className="py-3 px-4 text-slate-600 hover:text-teal-600 hover:bg-slate-50 rounded-lg transition-colors"
                >
                  {link.name}
                </a>
              ))}
              <a 
                href={`https://wa.me/${COMPANY_INFO.whatsapp}`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="mt-2"
              >
                <Button className="w-full bg-teal-600 hover:bg-teal-700 text-white rounded-full gap-2">
                  <WhatsAppIcon className="w-4 h-4" />
                  Get Quote on WhatsApp
                </Button>
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section id="home" data-testid="hero-section" className="relative min-h-screen pt-20 overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-orange-50/30" />
      
      <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center min-h-[calc(100vh-80px)] py-12 lg:py-0">
          {/* Left Content */}
          <motion.div 
            className="space-y-8"
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 bg-teal-50 text-teal-700 px-4 py-2 rounded-full text-sm font-medium">
              <Sun className="w-4 h-4" />
              Trusted by 1000+ Happy Customers
            </motion.div>
            
            <motion.h1 
              variants={fadeInUp}
              className="text-5xl md:text-6xl lg:text-7xl font-bold text-slate-900 leading-none tracking-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Transform Your <br />
              <span className="gradient-text">Space</span> Into Art
            </motion.h1>
            
            <motion.p 
              variants={fadeInUp}
              className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-xl"
            >
              Premium interior design and painting services. 
              We bring your vision to life with quality craftsmanship and attention to detail.
            </motion.p>
            
            <motion.div variants={fadeInUp} className="flex flex-col sm:flex-row gap-4">
              <a href="#contact">
                <Button 
                  data-testid="hero-cta-primary"
                  className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-8 py-6 text-lg shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                >
                  Get Free Consultation
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
              <a href="#projects">
                <Button 
                  data-testid="hero-cta-secondary"
                  variant="outline" 
                  className="border-2 border-slate-900 text-slate-900 hover:bg-slate-50 rounded-full px-8 py-6 text-lg"
                >
                  View Our Work
                </Button>
              </a>
            </motion.div>
            
            {/* Stats */}
            <motion.div variants={fadeInUp} className="flex gap-8 pt-8 border-t border-slate-200">
              <div>
                <p className="text-3xl font-bold text-slate-900">1000+</p>
                <p className="text-sm text-slate-500">Projects Done</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-slate-900">25+</p>
                <p className="text-sm text-slate-500">Years Experience</p>
              </div>
              <div>
                <p className="text-3xl font-bold text-slate-900">100%</p>
                <p className="text-sm text-slate-500">Satisfaction</p>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Image Grid */}
          <motion.div 
            className="relative h-[500px] lg:h-[600px]"
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="absolute inset-0 grid grid-cols-2 gap-4">
              {/* Main large image */}
              <div className="col-span-1 row-span-2 rounded-3xl overflow-hidden shadow-2xl img-zoom">
                <img 
                  src="https://images.unsplash.com/photo-1616593871468-2a9452218369?w=800&q=80"
                  alt="Modern Interior"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Top right image */}
              <div className="rounded-3xl overflow-hidden shadow-xl img-zoom">
                <img 
                  src="https://images.unsplash.com/photo-1612301988752-5a5b19021f45?w=600&q=80"
                  alt="Bedroom Design"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Bottom right image */}
              <div className="rounded-3xl overflow-hidden shadow-xl img-zoom">
                <img 
                  src="https://images.unsplash.com/photo-1589307693556-7286ae38293c?w=600&q=80"
                  alt="Painting Service"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            
            {/* Floating badge */}
            <motion.div 
              className="absolute -bottom-4 -left-4 bg-white rounded-2xl shadow-xl p-4 flex items-center gap-3"
              animate={{ y: [0, -10, 0] }}
              transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
            >
              <div className="w-12 h-12 bg-amber-100 rounded-full flex items-center justify-center">
                <Star className="w-6 h-6 text-amber-500 fill-amber-500" />
              </div>
              <div>
                <p className="font-bold text-slate-900">4.9 Rating</p>
                <p className="text-sm text-slate-500">Google Reviews</p>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  return (
    <section id="services" data-testid="services-section" className="py-24 md:py-32 bg-white">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Section Header */}
        <motion.div 
          className="text-center mb-16 max-w-3xl mx-auto space-y-4"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeInUp}
        >
          <span className="text-teal-600 font-medium tracking-wide uppercase text-sm">Our Services</span>
          <h2 
            className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            What We Offer
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed">
            From concept to completion, we provide comprehensive interior and painting solutions tailored to your needs.
          </p>
        </motion.div>

        {/* Services Grid */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          {SERVICES.map((service) => (
            <motion.div key={service.id} variants={fadeInUp}>
              <Card 
                data-testid={`service-card-${service.id}`}
                className={`service-card h-full border ${service.borderColor} hover:border-transparent bg-white`}
              >
                <CardContent className="p-8 flex flex-col items-start gap-4">
                  <div className={`w-14 h-14 rounded-2xl ${service.color} flex items-center justify-center`}>
                    <service.icon className="w-7 h-7" />
                  </div>
                  <h3 
                    className="text-xl font-bold text-slate-900"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {service.title}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">
                    {service.description}
                  </p>
                  <a 
                    href="#contact" 
                    className="mt-auto text-teal-600 font-medium flex items-center gap-1 hover:gap-2 transition-all"
                  >
                    Learn More <ChevronRight className="w-4 h-4" />
                  </a>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Projects Gallery Section
const ProjectsSection = () => {
  return (
    <section id="projects" data-testid="projects-section" className="py-24 md:py-32 bg-slate-50">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Section Header */}
        <motion.div 
          className="text-center mb-16 max-w-3xl mx-auto space-y-4"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeInUp}
        >
          <span className="text-teal-600 font-medium tracking-wide uppercase text-sm">Our Portfolio</span>
          <h2 
            className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Recent Projects
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed">
            Browse through our completed projects and get inspired for your next transformation.
          </p>
        </motion.div>

        {/* Bento Grid Gallery */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-12 gap-4"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          {PROJECTS.map((project) => (
            <motion.div 
              key={project.id} 
              variants={fadeInUp}
              className={`${project.span} rounded-2xl overflow-hidden group relative cursor-pointer`}
              data-testid={`project-card-${project.id}`}
            >
              <div className="aspect-[4/3] img-zoom">
                <img 
                  src={project.image}
                  alt={project.title}
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6">
                <span className="text-teal-400 text-sm font-medium">{project.category}</span>
                <h3 className="text-white text-xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>
                  {project.title}
                </h3>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA */}
        <motion.div 
          className="text-center mt-12"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
        >
          <a href={`https://wa.me/${COMPANY_INFO.whatsapp}?text=Hi, I'd like to see more of your projects.`} target="_blank" rel="noopener noreferrer">
            <Button 
              data-testid="projects-cta"
              variant="outline" 
              className="border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white rounded-full px-8 py-6"
            >
              View All Projects
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </a>
        </motion.div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="about" data-testid="about-section" className="py-24 md:py-32 bg-white">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Image */}
          <motion.div 
            className="relative"
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="rounded-3xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1589307693556-7286ae38293c?w=800&q=80"
                alt="Our Work"
                className="w-full h-[500px] object-cover"
              />
            </div>
            {/* Experience badge */}
            <motion.div 
              className="absolute -bottom-6 -right-6 bg-teal-600 text-white rounded-2xl p-6 shadow-xl"
              animate={{ y: [0, -5, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            >
              <p className="text-4xl font-bold">25+</p>
              <p className="text-sm opacity-90">Years Experience</p>
            </motion.div>
          </motion.div>

          {/* Content */}
          <motion.div 
            className="space-y-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.span variants={fadeInUp} className="text-teal-600 font-medium tracking-wide uppercase text-sm">
              About Us
            </motion.span>
            
            <motion.h2 
              variants={fadeInUp}
              className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Crafting Beautiful Spaces Since 1999
            </motion.h2>
            
            <motion.p variants={fadeInUp} className="text-lg text-slate-600 leading-relaxed">
              Smoravi Interiors is a Mumbai-based interior design and painting company dedicated to transforming 
              spaces into beautiful, functional environments. With over a decade of experience, we've helped 
              hundreds of families and businesses create their dream spaces.
            </motion.p>
            
            <motion.div variants={fadeInUp} className="space-y-4">
              {[
                "Premium quality materials & paints",
                "Experienced & professional team",
                "On-time project delivery",
                "Affordable pricing with no hidden costs",
                // "5-year warranty on all work"
              ].map((item, index) => (
                <div key={index} className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-teal-600" />
                  </div>
                  <p className="text-slate-700">{item}</p>
                </div>
              ))}
            </motion.div>
            
            <motion.div variants={fadeInUp}>
              <a href="#contact">
                <Button 
                  data-testid="about-cta"
                  className="bg-slate-900 hover:bg-slate-800 text-white rounded-full px-8 py-6 text-lg shadow-lg hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                >
                  Start Your Project
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  return (
    <section id="testimonials" data-testid="testimonials-section" className="py-24 md:py-32 bg-slate-900">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Section Header */}
        <motion.div 
          className="text-center mb-16 max-w-3xl mx-auto space-y-4"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeInUp}
        >
          <span className="text-teal-400 font-medium tracking-wide uppercase text-sm">Testimonials</span>
          <h2 
            className="text-4xl md:text-5xl font-bold text-white tracking-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            What Our Clients Say
          </h2>
          <p className="text-lg text-slate-400 leading-relaxed">
            Don't just take our word for it. Here's what our happy customers have to say.
          </p>
        </motion.div>

        {/* Testimonials Grid */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          {TESTIMONIALS.map((testimonial) => (
            <motion.div key={testimonial.id} variants={fadeInUp}>
              <Card 
                data-testid={`testimonial-card-${testimonial.id}`}
                className="testimonial-card bg-slate-800 border-slate-700 h-full"
              >
                <CardContent className="p-8 space-y-6">
                  {/* Stars */}
                  <div className="flex gap-1">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 text-amber-400 fill-amber-400" />
                    ))}
                  </div>
                  
                  {/* Content */}
                  <p className="text-slate-300 leading-relaxed">
                    "{testimonial.content}"
                  </p>
                  
                  {/* Author */}
                  <div className="flex items-center gap-4 pt-4 border-t border-slate-700">
                    <img 
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div>
                      <p className="font-bold text-white">{testimonial.name}</p>
                      <p className="text-sm text-slate-400">{testimonial.role}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Pricing Section
const PricingSection = () => {
  return (
    <section id="pricing" data-testid="pricing-section" className="py-24 md:py-32 bg-white">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Section Header */}
        <motion.div 
          className="text-center mb-16 max-w-3xl mx-auto space-y-4"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeInUp}
        >
          <span className="text-teal-600 font-medium tracking-wide uppercase text-sm">Pricing</span>
          <h2 
            className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Transparent Pricing
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed">
            Affordable rates with no hidden costs. Get the best value for quality work.
          </p>
        </motion.div>

        {/* Pricing Cards */}
        <motion.div 
          className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={staggerContainer}
        >
          {PRICING.map((plan) => (
            <motion.div key={plan.id} variants={fadeInUp}>
              <Card 
                data-testid={`pricing-card-${plan.id}`}
                className={`h-full ${plan.popular ? 'pricing-highlight' : 'border-slate-200'}`}
              >
                <CardContent className="p-8 flex flex-col h-full">
                  <div className="text-center space-y-2 mb-6">
                    <h3 
                      className="text-xl font-bold text-slate-900"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      {plan.title}
                    </h3>
                    <p className="text-sm text-slate-500">{plan.price}</p>
                    <div className="flex items-baseline justify-center gap-1">
                      <span className="text-4xl font-bold text-slate-900">{plan.amount}</span>
                      <span className="text-slate-500">{plan.unit}</span>
                    </div>
                  </div>
                  
                  <ul className="space-y-3 mb-8 flex-grow">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-3">
                        <Check className="w-5 h-5 text-teal-600 flex-shrink-0" />
                        <span className="text-slate-600">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  
                  <a href="#contact" className="mt-auto">
                    <Button 
                      className={`w-full rounded-full py-6 ${
                        plan.popular 
                          ? 'bg-teal-600 hover:bg-teal-700 text-white' 
                          : 'bg-slate-100 hover:bg-slate-200 text-slate-900'
                      }`}
                    >
                      Get Quote
                    </Button>
                  </a>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </motion.div>

        <motion.p 
          className="text-center text-slate-500 mt-8"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          variants={fadeInUp}
        >
          *Prices may vary based on project requirements. Contact us for a detailed quote.
        </motion.p>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    message: ""
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const subject = `New Inquiry from ${formData.name}`;
    const body = `Name: ${formData.name}%0D%0APhone: ${formData.phone}%0D%0AEmail: ${formData.email}%0D%0A%0D%0AMessage:%0D%0A${formData.message}`;
    window.location.href = `mailto:${COMPANY_INFO.email}?subject=${encodeURIComponent(subject)}&body=${body}`;
  };

  return (
    <section id="contact" data-testid="contact-section" className="py-24 md:py-32 bg-slate-50">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        {/* Section Header */}
        <motion.div 
          className="text-center mb-16 max-w-3xl mx-auto space-y-4"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeInUp}
        >
          <span className="text-teal-600 font-medium tracking-wide uppercase text-sm">Contact Us</span>
          <h2 
            className="text-4xl md:text-5xl font-bold text-slate-900 tracking-tight"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Let's Start Your Project
          </h2>
          <p className="text-lg text-slate-600 leading-relaxed">
            Ready to transform your space? Get in touch with us for a free consultation.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <Card className="border-slate-200 shadow-lg">
              <CardContent className="p-8">
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Your Name</label>
                    <Input 
                      data-testid="contact-name-input"
                      type="text"
                      placeholder="Enter your name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="contact-input rounded-xl py-6"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Phone Number</label>
                    <Input 
                      data-testid="contact-phone-input"
                      type="tel"
                      placeholder="Enter your phone number"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                      className="contact-input rounded-xl py-6"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Email Address</label>
                    <Input 
                      data-testid="contact-email-input"
                      type="email"
                      placeholder="Enter your email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="contact-input rounded-xl py-6"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Your Message</label>
                    <Textarea 
                      data-testid="contact-message-input"
                      placeholder="Tell us about your project..."
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      className="contact-input rounded-xl min-h-[120px]"
                      required
                    />
                  </div>
                  <Button 
                    data-testid="contact-submit-btn"
                    type="submit"
                    className="w-full bg-slate-900 hover:bg-slate-800 text-white rounded-full py-6 text-lg"
                  >
                    <Send className="w-5 h-5 mr-2" />
                    Send Message
                  </Button>
                </form>
              </CardContent>
            </Card>
          </motion.div>

          {/* Contact Info & Map */}
          <motion.div 
            className="space-y-8"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {/* Contact Cards */}
            <motion.div variants={fadeInUp} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Phone */}
              <a href={`tel:${COMPANY_INFO.phone}`} data-testid="contact-phone-link">
                <Card className="border-slate-200 hover:border-teal-200 hover:shadow-md transition-all cursor-pointer h-full">
                  <CardContent className="p-6 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-teal-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Call Us</p>
                      <p className="font-bold text-slate-900">{COMPANY_INFO.phoneDisplay}</p>
                    </div>
                  </CardContent>
                </Card>
              </a>

              {/* WhatsApp */}
              <a href={`https://wa.me/${COMPANY_INFO.whatsapp}`} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-link">
                <Card className="border-slate-200 hover:border-green-200 hover:shadow-md transition-all cursor-pointer h-full">
                  <CardContent className="p-6 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                      <WhatsAppIcon className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">WhatsApp</p>
                      <p className="font-bold text-slate-900">Chat Now</p>
                    </div>
                  </CardContent>
                </Card>
              </a>

              {/* Email */}
              <a href={`mailto:${COMPANY_INFO.email}`} data-testid="contact-email-link">
                <Card className="border-slate-200 hover:border-orange-200 hover:shadow-md transition-all cursor-pointer h-full">
                  <CardContent className="p-6 flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-orange-600" />
                    </div>
                    <div>
                      <p className="text-sm text-slate-500">Email Us</p>
                      <p className="font-bold text-slate-900 text-sm truncate">{COMPANY_INFO.email}</p>
                    </div>
                  </CardContent>
                </Card>
              </a>

              {/* Address */}
              <Card className="border-slate-200 h-full">
                <CardContent className="p-6 flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-rose-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Location</p>
                    <p className="font-bold text-slate-900">{COMPANY_INFO.address}</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* Map */}
            <motion.div variants={fadeInUp} className="map-container h-[300px]">
              <iframe
                src={COMPANY_INFO.mapUrl}
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Our Location"
              />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = () => {
  return (
    <footer data-testid="footer" className="bg-slate-900 text-white py-16">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="md:col-span-2 space-y-4">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-full bg-teal-600 flex items-center justify-center">
                <Palette className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl" style={{ fontFamily: "'Playfair Display', serif" }}>
                {COMPANY_INFO.name}
              </span>
            </div>
            <p className="text-slate-400 max-w-sm leading-relaxed">
              Transform your space with Mumbai's trusted interior design and painting experts. 
              Quality work at affordable prices.
            </p>
            <div className="flex gap-4">
              <a 
                href={`https://wa.me/${COMPANY_INFO.whatsapp}`} 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-10 h-10 rounded-full bg-slate-800 hover:bg-green-600 flex items-center justify-center transition-colors"
                data-testid="footer-whatsapp"
              >
                <WhatsAppIcon className="w-5 h-5" />
              </a>
              <a 
                href={`tel:${COMPANY_INFO.phone}`}
                className="w-10 h-10 rounded-full bg-slate-800 hover:bg-teal-600 flex items-center justify-center transition-colors"
                data-testid="footer-phone"
              >
                <Phone className="w-5 h-5" />
              </a>
              <a 
                href={`mailto:${COMPANY_INFO.email}`}
                className="w-10 h-10 rounded-full bg-slate-800 hover:bg-orange-600 flex items-center justify-center transition-colors"
                data-testid="footer-email"
              >
                <Mail className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="space-y-4">
            <h4 className="font-bold text-lg" style={{ fontFamily: "'Playfair Display', serif" }}>Quick Links</h4>
            <ul className="space-y-2">
              {["Home", "Services", "Projects", "About", "Pricing", "Contact"].map((link) => (
                <li key={link}>
                  <a 
                    href={`#${link.toLowerCase()}`}
                    className="text-slate-400 hover:text-white transition-colors"
                  >
                    {link}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div className="space-y-4">
            <h4 className="font-bold text-lg" style={{ fontFamily: "'Playfair Display', serif" }}>Services</h4>
            <ul className="space-y-2">
              {SERVICES.slice(0, 6).map((service) => (
                <li key={service.id}>
                  <a 
                    href="#services"
                    className="text-slate-400 hover:text-white transition-colors"
                  >
                    {service.title}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-slate-800 mt-12 pt-8 flex flex-col items-center gap-4">
          <p className="text-slate-400 text-sm">
            © {new Date().getFullYear()} {COMPANY_INFO.name}. All rights reserved.
          </p>
          <p className="text-white/40 text-xs sm:text-sm">
            Designed and Managed by{" "}
            <a
              href="https://devixelx.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-400 hover:text-blue-300 underline transition"
            >
              DevixelX
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
};

// Floating WhatsApp Button
const FloatingWhatsApp = () => {
  return (
    <motion.a
      href={`https://wa.me/${COMPANY_INFO.whatsapp}?text=Hi, I'm interested in your interior/painting services.`}
      target="_blank"
      rel="noopener noreferrer"
      className="whatsapp-float"
      data-testid="floating-whatsapp"
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      transition={{ delay: 1, type: "spring" }}
    >
      <div className="relative pulse-ring w-14 h-14 bg-[#25D366] rounded-full flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-110 transition-transform">
        <WhatsAppIcon className="w-7 h-7 text-white" />
      </div>
    </motion.a>
  );
};

// Main App Component
function App() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState("home");

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
      
      // Update active section based on scroll position
      const sections = ["home", "services", "projects", "about", "testimonials", "pricing", "contact"];
      for (const section of sections.reverse()) {
        const element = document.getElementById(section);
        if (element) {
          const rect = element.getBoundingClientRect();
          if (rect.top <= 150) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <div className="App" data-testid="app-container">
      <Header isScrolled={isScrolled} activeSection={activeSection} />
      <main>
        <HeroSection />
        <ServicesSection />
        <ProjectsSection />
        <AboutSection />
        <TestimonialsSection />
        {/* <PricingSection /> */}
        <ContactSection />
      </main>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}

export default App;
